<!--
  File name: PBNEFInputs.vue
  Program Description:
    - This file contains all the input fields in PBNEF form
    - This also includes all the validation per field
    - PBNEF data is fetch from the vuex store
-->
<template>
  <span>
    <v-row>
      <v-col cols="6">
        <v-autocomplete
          id="requestFor"
          v-model="arrayRequestFor"
          label="Type of Request"
          :outlined="!$meta.readOnly && isAllowedToEdit"
          :disabled="$meta.readOnly || !isAllowedToEdit"
          :items="typeOfRequests"
          :append-icon="''"
          :error-messages="genericError('requestFor', 'Type of Request')"
          @input="(value) => {
            $v.requestFor.$touch()
            if (!value.includes('Others')) {
              specify = ''
            } else {
              specify = typeOfRequestOther
            }
          }"
          @blur="$v.requestFor.$touch()"
        >
          <template #selection="{ item }">
            <v-chip
              color="#F0ECEC"
              style="color: #181C6C"
            >
              {{ formattedTypeOfRequestText(item) }}
            </v-chip>
          </template>
          <template #item="{ item, attrs, on }">
            <v-list-item
              v-bind="attrs"
              v-on="on"
            >
              {{ formattedTypeOfRequestText(item) }}
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="6">
        <v-text-field
          v-if="extensionDays && isExtensionSelected"
          :value="extensionDays"
          :outlined="!$meta.readOnly && isAllowedToEdit"
          label="Extension Days"
          disabled
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="6">
        <v-autocomplete
          id="dataSubjects"
          v-model="dataSubjects"
          label="Data Subjects"
          :outlined="!$meta.readOnly"
          :disabled="true"
          multiple
          deletable-chips
          small-chips
          :append-icon="''"
          :items="[
            'Own Employees',
            'Customers',
            'Personal data of vulnerable groups',
            'Others'
          ]"
          :error-messages="genericError('dataSubjects', 'Data Subjects')"
          @input="(value) => {
            $v.dataSubjects.$touch()
            if (!value.includes('Others')) {
              specifyIfOthers = ''
            }
            if (!value.includes('Personal data of vulnerable groups')) {
              specifyIfGroup = ''
            }
          }"
          @blur="$v.dataSubjects.$touch()"
        >
          <template #selection="{ item }">
            <v-chip
              color="#F0ECEC"
              style="color: #181C6C"
            >
              {{ item }}
            </v-chip>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col
        v-if="dataSubjects.includes('Others') && !withBothSpecify"
      >
        <v-textarea
          v-model="specifyIfOthers"
          label="Please specify (Others) in the Data Subjects"
          :outlined="!$meta.readOnly"
          disabled
          readonly
          rows="1"
          class="cursor"
          :error-messages="genericError('specifyIfOthers', 'Please specify (Others) in the Data Subjects')"
          @input="$v.specifyIfOthers.$touch()"
          @blur="$v.specifyIfOthers.$touch()"
        />
      </v-col>
      <v-col
        v-if="dataSubjects.includes('Personal data of vulnerable groups') && !withBothSpecify"
      >
        <v-textarea
          v-model="specifyIfGroup"
          label="Please specify (Personal data of vulnerable groups)"
          :outlined="!$meta.readOnly"
          disabled
          readonly
          rows="1"
          class="cursor"
          :error-messages="genericError('specifyIfGroup', 'Please specify (Personal data of vulnerable groups)')"
          @input="$v.specifyIfGroup.$touch()"
          @blur="$v.specifyIfGroup.$touch()"
        />
      </v-col>
    </v-row>
    <v-row v-if="withBothSpecify">
      <v-col
        v-if="dataSubjects.includes('Others')"
        cols="6"
      >
        <v-textarea
          v-model="specifyIfOthers"
          label="Please specify (Others) in the Data Subjects"
          :outlined="!$meta.readOnly"
          disabled
          readonly
          rows="1"
          class="cursor"
          :error-messages="genericError('specifyIfOthers', 'Please specify (Others) in the Data Subjects')"
          @input="$v.specifyIfOthers.$touch()"
          @blur="$v.specifyIfOthers.$touch()"
        />
      </v-col>
      <v-col
        v-if="dataSubjects.includes('Personal data of vulnerable groups')"
        cols="6"
      >
        <v-textarea
          v-model="specifyIfGroup"
          label="Please specify (Personal data of vulnerable groups)"
          :outlined="!$meta.readOnly"
          disabled
          readonly
          rows="1"
          class="cursor"
          :error-messages="genericError('specifyIfGroup', 'Please specify (Personal data of vulnerable groups)')"
          @input="$v.specifyIfGroup.$touch()"
          @blur="$v.specifyIfGroup.$touch()"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        v-if="requestFor.includes('Request for alternative means of notification')"
        cols="6"
      >
        <v-textarea
          :value="justification.alternative"
          outlined
          label="Justification for alternative means of notification"
          class="cursor"
          readonly
          disabled
        />
      </v-col>
      <v-col
        v-if="requestFor.includes('Request for exemption')"
        cols="6"
      >
        <v-textarea
          :value="justification.exemption"
          outlined
          label="Justification for exemption"
          class="cursor"
          readonly
          disabled
        />
      </v-col>
      <v-col
        v-if="requestFor.includes('Request for postponement')"
        cols="6"
      >
        <v-textarea
          :value="justification.postponement"
          outlined
          label="Justification for postponement"
          class="cursor"
          readonly
          disabled
        />
      </v-col>
      <v-col
        v-if="isExtensionSelected"
        cols="6"
      >
        <v-textarea
          :value="justification.extension"
          outlined
          label="Explain the reason for the request for extension of time to submit the required documents and specify what documents are included therein"
          class="cursor"
          readonly
          disabled
        />
      </v-col>
    </v-row>
    <h1 class="title primary--text py-4">
      No. of Data Subjects affected
    </h1>
    <v-row>
      <v-col cols="2">
        <v-text-field
          v-model="numberOfDataSubjectsAffected"
          type="number"
          label="No. of Data Subjects affected"
          :min="0"
          :outlined="!$meta.readOnly"
          :disabled="true"
          :error-messages="genericError('numberOfDataSubjectsAffected', 'No. of Data Subjects affected')"
          @input="(val) => {
            $v.numberOfDataSubjectsAffected.$touch()
            atLeastOneHundredIndividuals = (val >= 100)
          }"
          @blur="$v.numberOfDataSubjectsAffected.$touch()"
        />
      </v-col>
    </v-row>
    <v-checkbox
      v-model="atLeastOneHundredIndividuals"
      class="pa-0 ma-0"
      label="At least one hundred (100) individuals"
      :disabled="true"
    />
    <LabelError
      error-message="Please select at least one (1) Nature of Personal Data Involved."
      class="py-4"
      :show-error="$v.natureOfPersonalDataInvolved.checklist.$error"
      :bind="{
        class: 'title primary--text',
        style: 'font-size:19px'
      }"
    >
      Nature of Personal Data Involved
    </LabelError>
    <v-checkbox
      v-model="natureOfPersonalDataInvolved.checklist"
      :disabled="$meta.readOnly"
      class="pa-0 ma-0"
      label="Sensitive Personal Information Involved"
      value="Sensitive Personal Information Involved"
    />
    <v-row>
      <v-col>
        <h3 class="black--text">
          {{ $data.sensitivePersonalInformation.title }}
          <v-tooltip
            max-width="400"
            right
          >
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="gray"
                depressed
                v-bind="attrs"
                v-on="on"
              >
                mdi-information
              </v-icon>
            </template>
            <span>{{ $data.sensitivePersonalInformation.info }}</span>
          </v-tooltip>
        </h3>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        class="pb-0 "
        cols="3"
      >
        <v-text-field
          v-model="$data.sensitivePersonalInformation.provided"
          dense
          :class="$data.sensitivePersonalInformation.provided === 'No information yet' ? 'highlight' : ''"
          disabled
          label="Sufficiently Provided"
          outlined
        />
      </v-col>
      <v-col class="pb-0">
        <v-textarea
          v-model="$data.sensitivePersonalInformation.details"
          class="cursor"
          disabled
          readonly
          label="Provide Details"
          outlined
          rows="3"
        />
      </v-col>
    </v-row>
    <v-checkbox
      v-model="natureOfPersonalDataInvolved.checklist"
      :disabled="$meta.readOnly"
      class="pa-0 ma-0"
      label="Information that could enable identity fraud"
      value="Information that could enable identity fraud"
      :error-messages="customGenericError({
        showError: $v.natureOfPersonalDataInvolved.additionalChecklist.$error,
        errMessage: 'Please select at least one (1) sub-checkbox.'
      })"
      @change="$v.natureOfPersonalDataInvolved.additionalChecklist.$touch()"
    />
    <v-row>
      <v-col>
        <h3 class="black--text">
          {{ $data.identityFraud.title }}
          <v-tooltip
            max-width="400"
            right
          >
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="gray"
                depressed
                v-bind="attrs"
                v-on="on"
              >
                mdi-information
              </v-icon>
            </template>
            <span>{{ $data.identityFraud.info }}</span>
          </v-tooltip>
        </h3>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        class="pb-0 "
        cols="3"
      >
        <v-text-field
          v-model="$data.identityFraud.provided"
          dense
          :class="$data.identityFraud.provided === 'No information yet' ? 'highlight' : ''"
          disabled
          label="Sufficiently Provided"
          outlined
        />
      </v-col>
      <v-col class="pb-0">
        <v-textarea
          v-model="$data.identityFraud.details"
          class="cursor"
          disabled
          readonly
          label="Provide Details"
          outlined
          rows="3"
        />
      </v-col>
    </v-row>
    <div class="ml-12">
      <v-checkbox
        v-model="natureOfPersonalDataInvolved.additionalChecklist"
        :disabled="$meta.readOnly || !withSub"
        class="pa-0 ma-0"
        label="Data about the financial or economic situation of the data subject"
        value="Data about the financial or economic situation of the data subject"
      />
      <v-checkbox
        v-model="natureOfPersonalDataInvolved.additionalChecklist"
        :disabled="$meta.readOnly || !withSub"
        class="pa-0 ma-0"
        label="Usernames, passwords and other login data"
        value="Usernames, passwords and other login data"
      />
      <v-checkbox
        v-model="natureOfPersonalDataInvolved.additionalChecklist"
        :disabled="$meta.readOnly || !withSub"
        class="pa-0 ma-0"
        label="Biometric data"
        value="Biometric data"
      />
      <v-checkbox
        v-model="natureOfPersonalDataInvolved.additionalChecklist"
        :disabled="$meta.readOnly || !withSub"
        class="pa-0 ma-0"
        label="Copies of identification documents"
        value="Copies of identification documents"
      />
      <v-checkbox
        v-model="natureOfPersonalDataInvolved.additionalChecklist"
        :disabled="$meta.readOnly || !withSub"
        class="pa-0 ma-0"
        label="Licenses or unique identifiers like Philhealth, SSS, GSIS, TIN number"
        value="Licenses or unique identifiers like Philhealth, SSS, GSIS, TIN number"
      />
      <v-checkbox
        v-model="natureOfPersonalDataInvolved.additionalChecklist"
        :disabled="$meta.readOnly || !withSub"
        class="pa-0 ma-0"
        label="Information which may be made the basis of grant of rights or benefits"
        value="Information which may be made the basis of grant of rights or benefits"
      />
    </div>
    <v-checkbox
      v-model="natureOfPersonalDataInvolved.checklist"
      :disabled="$meta.readOnly"
      class="pa-0 ma-0"
      label="Information that would likely affect national security, public safety, public order, or public health"
      value="Information that would likely affect national security, public safety, public order, or public health"
    />
    <v-checkbox
      v-model="natureOfPersonalDataInvolved.checklist"
      :disabled="$meta.readOnly"
      class="pa-0 ma-0"
      label="Information required by applicable laws or rules be confidential"
      value="Information required by applicable laws or rules be confidential"
    />
    <v-checkbox
      v-model="natureOfPersonalDataInvolved.checklist"
      :disabled="$meta.readOnly"
      class="pa-0 ma-0"
      label="Others"
      value="Others"
    />
    <v-textarea
      v-show="withOther"
      v-model="natureOfPersonalDataInvolved.specify"
      class="cursor"
      label="Specify"
      rows="5"
      outlined
      :disabled="$meta.readOnly"
      :readonly="$meta.readOnly"
      :error-messages="genericError('natureOfPersonalDataInvolved.specify', 'Specify')"
      @input="$v.natureOfPersonalDataInvolved.specify.$touch()"
      @blur="$v.natureOfPersonalDataInvolved.specify.$touch()"
    />
    <span
      v-show="natureOfPersonalDataInvolved.checklist.includes('Sensitive Personal Information Involved') || withSub"
      class="text-subtitle-2 font-weight-bold primary--text py-4"
    >
      Please specify personal information and/or sensitive personal information involved
    </span>
    <v-col
      v-show="natureOfPersonalDataInvolved.checklist.includes('Sensitive Personal Information Involved') || withSub"
      cols="12"
    >
      <v-textarea
        v-model="natureOfPersonalDataInvolved.remarks"
        class="cursor"
        label="Remarks"
        rows="5"
        outlined
        :disabled="$meta.readOnly"
        :readonly="$meta.readOnly"
        :error-messages="genericError('natureOfPersonalDataInvolved.remarks', 'Remarks')"
        @input="$v.natureOfPersonalDataInvolved.remarks.$touch()"
        @blur="$v.natureOfPersonalDataInvolved.remarks.$touch()"
      />
    </v-col>
    <template v-for="(item, i) in measuresToFlat">
      <v-row :key="`${i}-title`">
        <v-col>
          <h3 class="black--text">
            {{ $data[`${item}`].title }}
            <v-tooltip
              max-width="400"
              right
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  color="gray"
                  depressed
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-information
                </v-icon>
              </template>
              <span>{{ $data[`${item}`].info }}</span>
            </v-tooltip>
          </h3>
        </v-col>
      </v-row>
      <v-row :key="`${i}-field`">
        <v-col
          class="pb-0 "
          cols="3"
        >
          <v-text-field
            v-model="$data[`${item}`].provided"
            dense
            :class="$data[`${item}`].provided === 'No information yet' ? 'highlight' : ''"
            disabled
            label="Sufficiently Provided"
            outlined
          />
        </v-col>
        <v-col class="pb-0">
          <v-textarea
            v-model="$data[`${item}`].details"
            class="cursor"
            disabled
            readonly
            label="Provide Details"
            outlined
            rows="3"
          />
        </v-col>
      </v-row>
    </template>
    <h1 class="title primary--text py-4">
      Measures in Place
    </h1>
    <v-checkbox
      v-model="measuresInPlace.checklist"
      :disabled="$meta.readOnly"
      class="pa-0 ma-0"
      label="Security measures would prevent unauthorized use or access of the personal data."
      value="Security measures would prevent unauthorized use or access of the personal data."
    />
    <v-checkbox
      v-model="measuresInPlace.checklist"
      :disabled="$meta.readOnly"
      class="pa-0 ma-0"
      label="Subsequent measures taken would ensure that the risk of harm or negative consequence to the data subjects will not materialize."
      value="Subsequent measures taken would ensure that the risk of harm or negative consequence to the data subjects will not materialize."
    />
    <v-col cols="12">
      <v-textarea
        v-model="measuresInPlace.remarks"
        class="cursor"
        label="Remarks"
        rows="5"
        outlined
        :readonly="$meta.readOnly"
        :disabled="$meta.readOnly"
      />
    </v-col>
    <template v-for="(item, i) in objToFlat">
      <v-row :key="`${i}title`">
        <v-col>
          <h3 class="black--text">
            {{ $data[`${item}`].title }}
            <v-tooltip
              max-width="400"
              right
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  color="gray"
                  depressed
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-information
                </v-icon>
              </template>
              <span>{{ $data[`${item}`].info }}</span>
            </v-tooltip>
          </h3>
        </v-col>
      </v-row>
      <v-row :key="i">
        <v-col
          class="pb-0 "
          cols="3"
        >
          <v-text-field
            v-model="$data[`${item}`].provided"
            dense
            :class="$data[`${item}`].provided === 'No information yet' ? 'highlight' : ''"
            disabled
            label="Sufficiently Provided"
            outlined
          />
        </v-col>
        <v-col class="pb-0">
          <v-textarea
            v-model="$data[`${item}`].details"
            class="cursor"
            disabled
            readonly
            label="Provide Details"
            outlined
            rows="3"
          />
        </v-col>
      </v-row>
    </template>
    <v-col class="py-0 mb-5">
      <LabelError
        error-message="Evaluated is required."
        class="pb-4"
        :show-error="$v.evaluation.$error"
        :bind="{
          class: 'gray--text',
          style: 'font-size:16px'
        }"
      >
        Evaluated
      </LabelError>
      <v-row>
        <v-col cols="3">
          <input
            v-model="evaluation"
            type="radio"
            value="Granted"
            :disabled="$meta.readOnly"
          >
          <span
            class="pa-3"
            style="font-size: 17px"
          >
            Granted
          </span>
        </v-col>
        <v-col cols="3">
          <input
            v-model="evaluation"
            type="radio"
            value="Denied"
            :disabled="$meta.readOnly"
          >
          <span
            class="pa-3"
            style="font-size: 17px"
          >
            Denied
          </span>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12">
      <v-textarea
        v-model="explanation"
        class="cursor"
        label="Explanation"
        rows="5"
        outlined
        :readonly="$meta.readOnly"
        :disabled="$meta.readOnly"
        :error-messages="genericError('explanation', 'Explanation')"
        @input="$v.explanation.$touch()"
        @blur="$v.explanation.$touch()"
      />
    </v-col>
    <v-row
      v-if="(isAssignee && (forFinalReview || forApproval)) || (fileUpload && (fileUpload.size || Object.keys(fileUpload).length))"
      align-self="start"
    >
      <v-col
        class="pa-10"
        cols="6"
      >
        <h3 class="gray--text py-4 mb-4">
          File Upload
        </h3>
        <v-row class="mx-2">
          <v-file-input
            v-model="fileUpload"
            :show-size="true"
            prepend-icon="mdi-file"
            color="primary"
            accept=".pdf"
            placeholder="Resolution"
            :outlined="!$meta.readOnly && isAssignee"
            :disabled="$meta.readOnly || !isAssignee"
            :error-messages="genericError('fileUpload', 'File Upload')"
            @input="$v.fileUpload.$touch()"
            @blur="$v.fileUpload.$touch()"
          >
            <template v-slot:selection="{ text, file }">
              {{ text || file.originalname }}
            </template>
          </v-file-input>
          <v-btn
            v-if="fileUpload && (fileUpload.size || Object.keys(fileUpload).length) && $meta.readOnly"
            icon
            color="primary"
            class="mt-3"
            @click="download(`attachment/pbnef/${$route.params.id}`, fileUpload)"
          >
            <v-icon size="28">
              mdi-download
            </v-icon>
          </v-btn>
        </v-row>
      </v-col>
    </v-row>
    <v-row
      v-if="operation !== 'create' && !isPic"
      class="py-10 my-0 ml-3"
    >
      <v-col cols="3">
        <v-autocomplete
          id="evaluatedBy"
          v-model="evaluatedBy"
          append-icon=""
          :items="users.filter(x => x.userRole.includes('evaluator'))"
          item-value="_id"
          item-text="fullName"
          label="Evaluated By"
          disabled
        />
      </v-col>
      <v-col cols="3">
        <v-autocomplete
          id="initialReviewedBy"
          v-model="initialReviewedBy"
          append-icon=""
          :items="users.filter(x => x.userRole.includes('reviewer'))"
          item-value="_id"
          item-text="fullName"
          label="Reviewed By"
          disabled
        />
      </v-col>
      <v-col cols="3">
        <v-autocomplete
          id="finalReviewBy"
          v-model="finalReviewBy"
          append-icon=""
          :items="users.filter(x => x.userRole.includes('reviewer'))"
          item-value="_id"
          item-text="fullName"
          label="Reviewed By"
          disabled
        />
      </v-col>
      <v-col cols="3">
        <v-autocomplete
          id="approvedBy"
          v-model="approvedBy"
          append-icon=""
          :items="users.filter(x => x.userRole.includes('reviewer') || x.userRole.includes('adjudicationRecipient'))"
          item-value="_id"
          item-text="fullName"
          label="Approved/Rejected By"
          disabled
        />
      </v-col>
    </v-row>
  </span>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import { validationMixin, validationErrors, pbnef } from '@/utils/validations'
import pbnefMixin from '@/mixins/pbnefMixin'
import { toOrdinal } from '@/utils/helpers.js'

export default {
  name: 'ViewForm',
  mixins: [validationMixin, pbnefMixin],
  computed: {
    ...validationErrors,
    ...mapFields('pbnef', [
      '$meta',
      'requestFor',
      'specify',
      'dataSubjects',
      'specifyIfOthers',
      'specifyIfGroup',
      'numberOfDataSubjectsAffected',
      'atLeastOneHundredIndividuals',
      'natureOfPersonalDataInvolved',
      'measuresInPlace',
      'explanation',
      'evaluation',
      'typeOfRequests',
      'evaluatedBy',
      'initialReviewedBy',
      'finalReviewBy',
      'approvedBy',
      'users',
      'fileUpload',
      'isSubmitted',
      'typeOfRequestOther'
    ]),
    arrayRequestFor: {
      get () {
        return this.requestFor.length && this.requestFor[0]
      },
      set (requestString) {
        this.requestFor = [requestString]
      }
    },
    isExtensionSelected () {
      return this.requestFor.find(request => request.toLowerCase().includes('request for extension'))
    },
    operation () {
      return this.$route.params.operation
    },
    withBothSpecify () {
      return this.dataSubjects.includes('Others') && this.dataSubjects.includes('Personal data of vulnerable groups')
    },
    isAllowedToEdit () {
      return this.forInitialization && this.isEvaluator
    },
    withSub () {
      return this.natureOfPersonalDataInvolved.checklist.includes('Information that could enable identity fraud')
    },
    withOther () {
      return this.natureOfPersonalDataInvolved.checklist.includes('Others')
    },
    customGenericError () {
      return ({ showError, errMessage }) => showError ? [errMessage] : []
    },
    formattedTypeOfRequestText () {
      return text => {
        if (text === 'Request for exemption') return `${text} to notify data subject`
        if (!text.includes('Request for extension')) return text
        const numberOfRequest = text.split(' ')[0].replace(/[{()}]/g, '')
        if (isNaN(parseInt(numberOfRequest))) return text
        return toOrdinal(numberOfRequest) + ' Request for extension'
      }
    }
  },
  watch: {
    withSub (value) {
      if (!value) this.natureOfPersonalDataInvolved.additionalChecklist = []
    }

  },
  validations () {
    return pbnef(this)
  }
}
</script>

<style scoped>
  .cursor >>>  textarea{
    overflow-y: hidden !important;
    cursor: default !important
  }
  .highlight >>> fieldset {
    background-color: pink
  }
</style>
